import { atom } from "jotai"
import { PhotoId } from "@/store/undoable-state"
import { store } from "@/store/store"
import { projectAtom } from "../project/projects-state"
import { PreviewSyncService } from "./previews-sync"
import {
	Preview,
	PreviewId,
	PreviewInfo,
	PreviewSize,
	getPreviewId,
} from "@/store/previews/previews-types"
import { PreviewRecord, createPreviewRecord, storePreviewRecords } from "@/storage/indexeddb"
import { resizeImageToSize } from "@/core/utils/resize-image"
import { isFirefox } from "@/utils/browser"
import { vec2 } from "gl-matrix"
import { Sentry } from "@/telemetry/sentry"

export const getPreviewInfoFromRecord = (record: PreviewRecord): PreviewInfo => {
	return {
		projectId: record.projectId,
		photoId: record.photoId,
		data: record.data,
		size: record.size,
		tags: record.tags,
		digest: record.digest,
	}
}
export type UniquePhotoId = string

export const getUniquePhotoId = (photoId: PhotoId, projectId: string): UniquePhotoId => {
	return `${projectId}-${photoId}`
}

export const visibleThumbnailsAtom = atom<Set<PhotoId>>(new Set<PhotoId>())

export const previewsAtom = atom<Record<PreviewId, Preview>>({})

export const getPreview = (props: {
	photoId: PhotoId
	projectId: string
	size: PreviewSize
	edited?: boolean
}) => {
	const previews = store.get(previewsAtom)
	return previews[getPreviewId(props)]
}

export const getAvailablePreview = (props: {
	photoId: PhotoId
	projectId: string
	size: PreviewSize
}) => {
	const previews = store.get(previewsAtom)
	return previews[getPreviewId({ ...props, edited: true })] || previews[getPreviewId(props)]
}

export const cleanupPreviews = () => {
	const previews = store.get(previewsAtom)

	Object.values(previews).forEach((value) => {
		URL.revokeObjectURL(value.url)
	})

	store.set(previewsAtom, {})
}

const createPreviewObjectUrl = (data: Blob): string | undefined => {
	try {
		return URL.createObjectURL(data)
	} catch (error) {
		Sentry.captureException(error, { level: "warning" })
		return undefined
	}
}

const getUpdatedPreviews = (props: {
	previewsInfo: PreviewInfo[]
	createRecords: boolean
	isForced?: boolean
}) => {
	const { previewsInfo, createRecords, isForced } = props
	const existingPreviews = store.get(previewsAtom)
	const project = store.get(projectAtom)

	let shouldSyncToBE = false
	const updatedPreviews: Record<PreviewId, Preview> = {}
	const updatedPreviewRecords: PreviewRecord[] = []

	previewsInfo.forEach((info) => {
		const previewId = getPreviewId(info)
		const currentPreview = existingPreviews[previewId]
		if (
			!currentPreview ||
			isForced ||
			(info.tags.includes("edited") && info.digest !== currentPreview.digest) || // always update edited previews to latest
			(info.tags.includes("rendered") && currentPreview.tags.includes("extracted")) // always update rendered previews to overwrite extracted
		) {
			if (!(info.data instanceof Blob)) {
				Sentry.captureException(new Error("[Previews] Invalid preview payload type"), {
					level: "warning",
				})
				return
			}
			const objectUrl = createPreviewObjectUrl(info.data)
			if (!objectUrl) {
				return
			}

			const newPreview: Preview = {
				url: objectUrl,
				digest: info.digest,
				size: info.size,
				storageSize: info.data.size,
				tags: info.tags,
			}

			if (currentPreview) {
				URL.revokeObjectURL(currentPreview.url)
			}

			updatedPreviews[previewId] = newPreview

			if (createRecords) {
				updatedPreviewRecords.push(createPreviewRecord(info))
			}

			if (
				project &&
				project.id === info.projectId &&
				project.previewThumbnailPhotoIds?.includes(info.photoId) &&
				info.size === PreviewSize.medium
			) {
				shouldSyncToBE = true
			}
		}
	})

	return { updatedPreviews, updatedPreviewRecords, shouldSyncToBE }
}

export function updatePreviews(props: {
	previewsInfo: PreviewInfo[]
	skipStoreToDB?: boolean
	isForced?: boolean
}) {
	const { previewsInfo, skipStoreToDB, isForced } = props
	const { updatedPreviews, updatedPreviewRecords, shouldSyncToBE } = getUpdatedPreviews({
		previewsInfo,
		createRecords: !skipStoreToDB,
		isForced,
	})

	//Updates atom
	if (Object.keys(updatedPreviews).length > 0) {
		store.set(previewsAtom, (prev) => ({
			...prev,
			...updatedPreviews,
		}))
	}

	// Sync to BE
	const project = store.get(projectAtom)
	if (shouldSyncToBE && project) {
		PreviewSyncService.syncProjectCoverIfNeededDebounced(project)
	}
	//Stores in db
	if (!skipStoreToDB && updatedPreviewRecords.length > 0) {
		void storePreviewRecords(updatedPreviewRecords).catch((e) => Sentry.captureException(e))
	}
}

export async function resizeAndStoreSmallPreview(previewInfo: PreviewInfo, originalSize?: vec2) {
	if (previewInfo.size === PreviewSize.small) {
		return
	}
	try {
		// Firefox neuters blob data when the source canvas is reused; clone to stabilise.
		const imageData = isFirefox
			? new Blob([await previewInfo.data.arrayBuffer()], { type: previewInfo.data.type })
			: previewInfo.data
		const smallData = await resizeImageToSize({
			image: imageData,
			options: { outputQuality: 0.5 },
			longestSide: 512,
			originalSize,
		})

		const smallPreview: PreviewInfo = {
			...previewInfo,
			data: smallData,
			size: PreviewSize.small,
		}

		return updatePreviews({ previewsInfo: [smallPreview] })
	} catch (e) {
		console.warn("Failed to resize preview to small", e)
	}
}
